<template>
  <v-dialog
      v-model="dialog"
      max-width="800px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5" style="padding-top: 10px;">{{ $t('schedule.importBatchTitle') }}</span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text style="padding: 24px;">
        <v-container fluid class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <!-- Download Template Link -->
              <div class="mb-4">
                <a
                  href="javascript:void(0)"
                  :class="['download-template-link', { 'disabled-link': countdown > 0 }]"
                  @click="countdown === 0 && onDownloadTemplate()"
                >
                  {{ $t('schedule.downloadExcelTemplate') }}
                  <span v-if="countdown > 0" style="margin-left: 8px;">({{ countdown }}s)</span>
                </a>
              </div>

              <!-- Display selected file or Upload Zone -->
              <div v-if="!file">
                <!-- Custom Upload Zone -->
                <v-sheet
                  class="upload-zone"
                  :class="{ 'drag-over': isDragOver }"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                  @click="$refs.fileInput.click()">
                  <b-img
                  class="mr-2"
                  :src="require('@/assets/images/import-schedule.svg')"
                  width="48"
                  height="48"
                  />
                  <div class="upload-text">{{ $t('schedule.clickToUpload') }}</div>
                  <div class="file-format">{{ $t('schedule.fileFormat') }}</div>
                  <input
                    ref="fileInput"
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    @change="handleFileSelect">
                </v-sheet>
              </div>

              <!-- Display selected file with new design -->
              <div v-else class="file-display-box">
                <div class="d-flex align-center">
                  <img
                    :src="require('@/assets/images/excel-icon.svg')"
                    width="70"
                    height="70"
                    class="mr-3"
                    alt="Excel icon"
                  />
                  <span class="file-name">{{ file.name }}</span>
                </div>
                <a
                  href="javascript:void(0)"
                  class="upload-another-link"
                  @click="removeFile"
                >
                  {{ $t('schedule.uploadAnotherFile') }}
                </a>
              </div>

              <!-- Error messages -->
              <div v-if="fileErrors.length > 0" class="mt-2">
                <v-alert type="error" dense outlined>
                  <div v-for="error in fileErrors" :key="error">{{ error }}</div>
                </v-alert>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="importResult">
            <v-col cols="12">
              <v-alert
                  :type="importResult.failed > 0 ? 'warning' : 'success'"
                  text>
                <div>{{ $t('schedule.totalRows') }}: {{ importResult.total }}</div>
                <div>{{ $t('schedule.successRows') }}: {{ importResult.success }}</div>
                <div>{{ $t('schedule.failedRows') }}: {{ importResult.failed }}</div>
              </v-alert>

              <v-simple-table v-if="importResult.errors && importResult.errors.length > 0" dense>
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th>{{ $t('schedule.row') }}</th>
                    <!-- <th>{{ $t('schedule.email') }}</th> -->
                    <th>{{ $t('schedule.error') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="error in importResult.errors" :key="error.row">
                    <td>{{ error.row }}</td>
                    <!-- <td>{{ error.email }}</td> -->
                    <td>{{ error.error }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider style="margin-bottom: 0px;"></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            outlined
            elevation="0"
            class="no-uppercase"
            @click="close">
          {{ $t('schedule.cancel') }}
        </v-btn>
        <v-btn
            elevation="0"
            color="primary"
            class="no-uppercase"
            :disabled="!file || uploading || uploadDisabled"
            :loading="uploading"
            @click="importFile">
          {{ $t('schedule.upload') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {API2} from "../../api";
import mixin from "../../mixin";
import axios from "axios";
import store from "../../store";

const COUNTDOWN_STORAGE_KEY = 'SCHEDULE_DOWNLOAD_COUNTDOWN';

export default {
  name: "ImportDialog",
  mixins: [mixin],
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      uploading: false,
      fileErrors: [],
      importResult: null,
      isDragOver: false,
      countdown: 0,
      countdownTimer: null,
      uploadDisabled: false,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.resetForm();
      } else {
        // Khi mở dialog, khôi phục countdown từ localStorage
        this.restoreCountdown();
      }
    }
  },
  methods: {
    close() {
      this.dialog = false;
    },
    resetForm() {
      this.file = null;
      this.fileErrors = [];
      this.importResult = null;
      this.uploading = false;
      this.uploadDisabled = false;
      this.isDragOver = false;
      // KHÔNG reset countdown khi close dialog để giữ trạng thái
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    resetCountdown() {
      this.countdown = 0;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      // Xóa khỏi localStorage
      localStorage.removeItem(COUNTDOWN_STORAGE_KEY);
    },
    restoreCountdown() {
      const savedData = localStorage.getItem(COUNTDOWN_STORAGE_KEY);
      if (savedData) {
        try {
          const { endTime } = JSON.parse(savedData);
          const now = Date.now();
          const remainingTime = Math.max(0, Math.ceil((endTime - now) / 1000));
          
          if (remainingTime > 0) {
            this.countdown = remainingTime;
            this.startCountdownTimer();
          } else {
            // Countdown đã hết, xóa khỏi localStorage
            this.resetCountdown();
          }
        } catch (error) {
          this.resetCountdown();
        }
      }
    },
    startCountdown() {
      const endTime = Date.now() + (30 * 1000); // 30 giây từ bây giờ
      this.countdown = 30;
      
      // Lưu vào localStorage
      localStorage.setItem(COUNTDOWN_STORAGE_KEY, JSON.stringify({
        endTime: endTime
      }));
      
      this.startCountdownTimer();
    },
    startCountdownTimer() {
      // Clear timer cũ nếu có
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          // Xóa khỏi localStorage khi countdown về 0
          localStorage.removeItem(COUNTDOWN_STORAGE_KEY);
        }
      }, 1000);
    },
    async onDownloadTemplate() {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const filename = `Import_Template_DutySchedule_${year}-${month}-${day}_${hours}${minutes}.xlsx`;

        await this.downloadFile(API2.SCHEDULE_TEMPLATE, {
          optFileName: filename
        });
        this.showToast("success", this.$t("base.notification"), this.$t("schedule.downloadTemplateSuccess"));
        this.startCountdown();
      } catch (error) {
        this.showToast("danger", this.$t("ui.anErrorOccurred"), this.$t("schedule.downloadTemplateError"));
      }
    },
    clearErrors() {
      this.fileErrors = [];
      this.importResult = null;
    },
    handleDragOver(e) {
      e.preventDefault();
      this.isDragOver = true;
    },
    handleDragLeave(e) {
      e.preventDefault();
      this.isDragOver = false;
    },
    handleDrop(e) {
      e.preventDefault();
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },
    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },
    handleFile(file) {
      // Validate file type
      const validTypes = ['.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(fileExtension)) {
        this.fileErrors = [this.$t('schedule.invalidFileType')];
        return;
      }
      
      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.fileErrors = [this.$t('schedule.fileTooLarge')];
        return;
      }
      
      this.file = file;
      this.uploadDisabled = false;
      this.clearErrors();
    },
    removeFile() {
      this.file = null;
      this.uploadDisabled = false;
      this.$refs.fileInput.value = '';
      this.clearErrors();
    },
    async importFile() {
      if (!this.file) {
        this.fileErrors = [this.$t('schedule.selectFile')];
        return;
      }

      this.uploading = true;
      this.fileErrors = [];
      this.importResult = null;

      const formData = new FormData();
      formData.append('file', this.file);

      try {
        const token = store.state.token;
        const lang = store.state.locale;
        
        const response = await axios.post(API2.SCHEDULE_IMPORT, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': token,
            'Accept-Language': lang
          }
        });

        if (response.data && response.data.status === 200) {
          this.importResult = response.data.data;

          if (this.importResult.failed === 0) {
            this.$emit('imported');
            setTimeout(() => {
              this.close();
            }, 2000);
          }
          // If there are failed rows, disable the upload button to force user to upload a corrected file
          if (this.importResult && this.importResult.failed > 0) {
            this.uploadDisabled = true;
          }
        } else {
          this.showToast('danger', this.$t('ui.anErrorOccurred'), response.data.error?.message || this.$t('schedule.importError'));
          this.uploadDisabled = true;
        }
      } catch (error) {
        this.showToast('danger', this.$t('ui.anErrorOccurred'), error.message || this.$t('schedule.importError'));
        this.uploadDisabled = true;
      } finally {
        this.uploading = false;
      }
    }
  },
  // Cleanup timer khi component bị destroy
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }
};
</script>

<style scoped>
.upload-zone {
  border: 1px solid #D9D9D9;
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-zone:hover,
.upload-zone.drag-over {
  border-color: #1976d2;
  background-color: #f3f8ff;
}

.upload-text {
  font-size: 14px;
  color: #424242;
  margin: 16px 0 8px 0;
  font-weight: 400;
}

.file-format {
  font-size: 12px;
  color: #757575;
  margin-top: 4px;
}

.download-template-link {
  color: #1976d2;
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
}

.download-template-link:hover {
  text-decoration: underline;
}

.download-template-link.disabled-link {
  color: #9e9e9e;
  cursor: not-allowed;
  pointer-events: none;
}

/* File display box styling */
.file-display-box {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px 20px;
  background-color: #FAFAFA;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-name {
  font-size: 14px;
  color: #1F2937;
  font-weight: 400;
}

.upload-another-link {
  color: #1976d2;
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
}

.upload-another-link:hover {
  text-decoration: underline;
}

/* Button styling to match design */
.v-btn.v-btn--outlined {
  border: 1px solid #d4d4d8;
  color: #71717a;
}

.v-btn.v-btn--outlined:hover {
  background-color: #f4f4f5;
}

/* Card actions spacing */
.v-card__actions {
  padding: 16px 24px;
}

/* Dialog responsive width */
@media (max-width: 600px) {
  .upload-zone {
    padding: 32px 16px;
    min-height: 140px;
  }

  .upload-text {
    font-size: 14px;
  }

  .file-display-box {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>